import { AbstractCurrentSubscriptionModel } from '../../api/billing-types';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { UseFormWatch } from 'react-hook-form';
import DriveIcon from '../../assets/subscriptions/drive.png';
import GeminiIcon from '../../assets/subscriptions/gemini.svg';
import GPanelIcon from '../../assets/subscriptions/gpanel.svg';
import GSuiteIcon from '../../assets/subscriptions/gsuite.svg';
import JamboardIcon from '../../assets/subscriptions/jamboard.png';
import VaultIcon from '../../assets/subscriptions/vault.png';
import { CheckoutModelSchema } from './billing-form-page';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

interface ReviewStepProps {
  subscription: AbstractCurrentSubscriptionModel;
  agreeToTermsCheckbox: boolean;
  setAgreeToTermsCheckbox: Dispatch<SetStateAction<boolean>>;
  watch: UseFormWatch<CheckoutModelSchema>;
  bundledGpanelSubscription: AbstractCurrentSubscriptionModel | null;
}

export const ReviewStep = ({
  subscription,
  agreeToTermsCheckbox,
  setAgreeToTermsCheckbox,
  watch,
  bundledGpanelSubscription,
}: ReviewStepProps) => {
  const items = watch('items');
  const hasMoreThanOneLineItem = items.length > 1;
  const additionalLicensesCount = items[0].qty || 0;
  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const isMonthlyPrepay = subscription.billingType === 'monthlyPrepay';
  const isgPanelSubMonthlyPrepay =
    bundledGpanelSubscription?.billingType === 'monthlyPrepay';

  const getImage = useCallback((imageUrl: string) => {
    const url = imageUrl.toLowerCase();

    if (url.includes('gsuite')) {
      return GSuiteIcon;
    } else if (url.includes('gpanel')) {
      return GPanelIcon;
    } else if (url.includes('jamboard')) {
      return JamboardIcon;
    } else if (url.includes('vault')) {
      return VaultIcon;
    } else if (url.includes('drive')) {
      return DriveIcon;
    } else if (url.includes('gemini')) {
      return GeminiIcon;
    } else {
      return GSuiteIcon;
    }
  }, []);

  interface Row {
    name: string;
    productImageUrl: string;
    pricePerUnit: number;
    commitmentType: string;
    quantity: number;
    price: number;
    charges: number;
  }

  const subscriptionProratedPricePerUnit = isMonthlyPrepay
    ? 0
    : subscription.proratedPricePerUnit;

  const bundledGpanelSubscriptionProratedPricePerUnit = isgPanelSubMonthlyPrepay
    ? 0
    : bundledGpanelSubscription?.proratedPricePerUnit;

  const rows: Row[] = [
    {
      name: subscription.productModel.productName,
      productImageUrl: subscription.productModel.productImageUrl,
      pricePerUnit: subscription.pricePerUnit,
      commitmentType: subscription.commitmentPlan,
      quantity: additionalLicensesCount,
      price: subscriptionProratedPricePerUnit,
      charges: subscriptionProratedPricePerUnit * additionalLicensesCount,
    },
    ...(hasMoreThanOneLineItem && items[1].qty > 0
      ? items.slice(1).map((item) => ({
          name: bundledGpanelSubscription?.productModel.productName || '',
          productImageUrl:
            bundledGpanelSubscription?.productModel.productImageUrl || '',
          pricePerUnit: bundledGpanelSubscription?.pricePerUnit || 0,
          commitmentType: bundledGpanelSubscription?.commitmentPlan || '',
          quantity: item.qty,
          price: bundledGpanelSubscriptionProratedPricePerUnit || 0,
          charges:
            (bundledGpanelSubscriptionProratedPricePerUnit || 0) * item.qty,
        }))
      : []),
  ];

  const totalCharges = rows.reduce(
    (sum, row) => sum + (row.charges ? row.charges : 0),
    0
  );

  return (
    <Stack gap={2}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Price / Unit
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Commitment Type
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                License Count
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Prorated Price
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Charges
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.name}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <img
                    src={getImage(row.productImageUrl)}
                    alt={row.name}
                    style={{ width: 25, height: 25 }}
                  />
                  {row.name}
                </TableCell>
                <TableCell align="right">
                  {priceFormatter.format(row.pricePerUnit)}
                </TableCell>
                <TableCell align="right">{row.commitmentType}</TableCell>
                <TableCell align="right">{row.quantity}</TableCell>
                <TableCell align="right">
                  {priceFormatter.format(row.price)}
                </TableCell>
                <TableCell align="right">
                  {priceFormatter.format(row.charges)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell rowSpan={4} />

              <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                Total:
              </TableCell>
              <TableCell align="right">
                <Stack>
                  {priceFormatter.format(totalCharges)}
                  <Typography variant="caption">(plus tax)</Typography>
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <FormControlLabel
        control={
          <Checkbox
            checked={agreeToTermsCheckbox}
            onChange={() => setAgreeToTermsCheckbox(!agreeToTermsCheckbox)}
          />
        }
        label={
          <>
            I have read and agree to the terms of the{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="https://docs.google.com/document/d/1F4Wn4E8nrEQYZ3pIqB7eZhmYp2LkG7K6iZHTwHQz0KU/"
            >
              Google Workspace
            </Link>{' '}
            agreement.
          </>
        }
      />
    </Stack>
  );
};
